import { decode, isBlurhashValid } from "blurhash";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { twJoin } from "tailwind-merge";

import type { ImageDto } from "@/api/types";
import { MediaSkeleton } from "@/design-system/MediaSkeleton";

const DECODED_IMAGE_SIZE = 32;

type ImagePlaceholderProps = {
  image: ImageDto | undefined;
  objectFit?: "contain" | "cover";
};

export function ImagePlaceholder({ image, objectFit = "cover" }: ImagePlaceholderProps): ReactNode {
  const blurHash = image?.blurHash;
  if (!blurHash || !isBlurhashValid(blurHash).result) {
    return <MediaSkeleton />;
  }

  const imageWidth = image.metadata.width;
  const imageHeight = image.metadata.height;
  const aspectRatio = imageWidth && imageHeight ? imageWidth / imageHeight : 1;
  const decodedWidth =
    aspectRatio >= 1 ? DECODED_IMAGE_SIZE : Math.max(1, Math.round(DECODED_IMAGE_SIZE * aspectRatio));
  const decodedHeight =
    aspectRatio >= 1 ? Math.max(1, Math.round(DECODED_IMAGE_SIZE / aspectRatio)) : DECODED_IMAGE_SIZE;

  return <BlurHashCanvas blurHash={blurHash} width={decodedWidth} height={decodedHeight} objectFit={objectFit} />;
}

function BlurHashCanvas({
  blurHash,
  width,
  height,
  objectFit,
}: {
  blurHash: string;
  width: number;
  height: number;
  objectFit: "contain" | "cover";
}): ReactNode {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    // Synchronize decoded pixels with the browser canvas while this placeholder is rendered.
    const context = canvasRef.current?.getContext("2d");
    if (!context) {
      return;
    }

    const pixels = new Uint8ClampedArray(decode(blurHash, width, height));
    context.putImageData(new ImageData(pixels, width, height), 0, 0);
  }, [blurHash, height, width]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={twJoin(
        "pointer-events-none absolute top-0 left-0 z-raised size-full",
        objectFit === "contain" ? "object-contain" : "object-cover",
      )}
      aria-hidden="true"
    />
  );
}
